import './styles.css';
import { answerHatbotQuestion } from './decision-engine';
import { feedbackTags, scenarios } from './scenarios';
import type { FeedbackPayload, HatbotAnswer, PromptRunPayload, Scenario, TesterIdentity, TranscriptMessage, Verdict } from './types';

const app = document.querySelector<HTMLDivElement>('#app');

if (!app) {
  throw new Error('App root missing');
}

const appRoot = app;

const SESSION_KEY = 'hatbot-decision-lab-session';
const TESTER_KEY = 'hatbot-decision-lab-tester';
const HISTORY_KEY = 'hatbot-decision-lab-history';

type ApiState = 'checking' | 'online' | 'offline';

interface FeedbackHistoryItem {
  id: string;
  createdAt: string;
  scenarioId: string;
  verdict: Verdict;
}

let selectedScenario = scenarios[0];
let selectedGroup = 'All';
let transcript: TranscriptMessage[] = [openingMessage()];
let lastAnswer: HatbotAnswer | null = null;
let lastPrompt = '';
let apiState: ApiState = 'checking';
let feedbackHistory = readFeedbackHistory();
let feedbackStatusMessage = '';
let testerIdentity: TesterIdentity = {
  authenticated: false,
  name: '',
  email: '',
  subject: '',
};
let lastPromptRunId = '';
let pendingPromptRun: Promise<string> | null = null;
let activePromptRunToken = 0;

function openingMessage(): TranscriptMessage {
  return {
    role: 'assistant',
    content:
      "You're testing the live version of HatBot before it goes on the storefront. Ask it the same questions a customer would: custom hat timing, order status, appointments, ReCraft, product availability, or anything that should get handed to the GHW team. After each answer, use the trace and feedback form to mark what needs tightening before launch.",
  };
}

function getSessionId(): string {
  const existing = sessionStorage.getItem(SESSION_KEY);
  if (existing) return existing;
  const id = crypto.randomUUID();
  sessionStorage.setItem(SESSION_KEY, id);
  return id;
}

function startNewSession(): void {
  sessionStorage.setItem(SESSION_KEY, crypto.randomUUID());
  void recordSession();
}

function readFeedbackHistory(): FeedbackHistoryItem[] {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) ?? '[]') as FeedbackHistoryItem[];
  } catch {
    return [];
  }
}

function writeFeedbackHistory(next: FeedbackHistoryItem[]): void {
  feedbackHistory = next.slice(0, 12);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(feedbackHistory));
}

function currentTesterName(): string {
  return testerIdentity.name || localStorage.getItem(TESTER_KEY) || '';
}

function currentContext(): PromptRunPayload['context'] {
  return {
    order: selectedScenario.order,
    product: selectedScenario.product,
  };
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function formatMessage(value: string): string {
  return value
    .split('\n')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => `<p>${escapeHtml(part)}</p>`)
    .join('');
}

function statusLabel(): string {
  if (apiState === 'online') return 'Recording online';
  if (apiState === 'offline') return 'Recording offline';
  return 'Checking recording';
}

function statusClass(): string {
  if (apiState === 'online') return 'is-online';
  if (apiState === 'offline') return 'is-offline';
  return 'is-checking';
}

function filteredScenarios(): Scenario[] {
  if (selectedGroup === 'All') return scenarios;
  return scenarios.filter((scenario) => scenario.group === selectedGroup);
}

function groups(): string[] {
  return ['All', ...Array.from(new Set(scenarios.map((scenario) => scenario.group)))];
}

function render(): void {
  const testerName = currentTesterName();
  appRoot.innerHTML = `
    <div class="app-shell">
      <header class="topbar">
        <div class="brand-row">
          <img class="ghw-logo" src="https://greeleyhatworks.com/cdn/shop/files/GHWLogo2022.png?height=180&v=1701054226&width=360" alt="Greeley Hat Works" />
          <div>
            <p class="eyebrow">Tester surface</p>
            <h1>HatBot Decision Lab</h1>
          </div>
        </div>
        <div class="status-row">
          <span class="status-pill">No live Shopify data</span>
          <span class="status-pill">No HatMail mutation</span>
          ${testerIdentity.authenticated ? `<span class="status-pill is-online">${escapeHtml(testerIdentity.email || testerIdentity.name)}</span>` : ''}
          <span class="status-pill ${statusClass()}">${statusLabel()}</span>
        </div>
      </header>

      <main class="workspace">
        <aside class="panel scenario-panel" aria-label="Scenario queue">
          <div class="panel-header">
            <div>
              <p class="eyebrow">Queue</p>
              <h2>Scenarios</h2>
            </div>
            <span class="count-badge">${filteredScenarios().length}</span>
          </div>
          <div class="segmented" role="tablist" aria-label="Scenario groups">
            ${groups()
              .map(
                (group) => `
                  <button class="segment ${group === selectedGroup ? 'is-active' : ''}" type="button" data-group="${escapeHtml(group)}">
                    ${escapeHtml(group)}
                  </button>
                `,
              )
              .join('')}
          </div>
          <div class="scenario-list">
            ${filteredScenarios()
              .map(
                (scenario) => `
                  <button class="scenario-button ${scenario.id === selectedScenario.id ? 'is-active' : ''}" type="button" data-scenario="${scenario.id}">
                    <span class="scenario-id">${scenario.id}</span>
                    <strong>${escapeHtml(scenario.title)}</strong>
                    <span>${escapeHtml(scenario.expected)}</span>
                  </button>
                `,
              )
              .join('')}
          </div>
        </aside>

        <section class="panel chat-panel" aria-label="Conversation">
          <div class="panel-header">
            <div>
              <p class="eyebrow">${escapeHtml(selectedScenario.group)} / ${escapeHtml(selectedScenario.id)}</p>
              <h2>${escapeHtml(selectedScenario.title)}</h2>
            </div>
            <button class="icon-button" type="button" id="reset-chat" title="Reset conversation" aria-label="Reset conversation">Reset</button>
          </div>

          <div class="selected-brief">
            <div>
              <span class="meta-label">Expected</span>
              <p>${escapeHtml(selectedScenario.expected)}</p>
            </div>
            <button class="primary-action" type="button" id="run-scenario">Run Scenario</button>
          </div>

          <div class="messages" id="messages" aria-live="polite">
            ${transcript
              .map(
                (message) => `
                  <article class="message ${message.role === 'user' ? 'is-user' : 'is-assistant'}">
                    <div class="avatar" aria-hidden="true">${message.role === 'user' ? 'T' : 'H'}</div>
                    <div class="bubble">${formatMessage(message.content)}</div>
                  </article>
                `,
              )
              .join('')}
          </div>

          <form class="composer" id="question-form" autocomplete="off">
            <label class="sr-only" for="question-input">Prompt</label>
            <textarea id="question-input" rows="3" placeholder="Ask a test prompt">${escapeHtml(selectedScenario.prompt)}</textarea>
            <button type="submit">Send</button>
          </form>
        </section>

        <aside class="panel trace-panel" aria-label="Decision trace and feedback">
          <div class="panel-header">
            <div>
              <p class="eyebrow">Decision</p>
              <h2>Trace</h2>
            </div>
          </div>
          ${renderTrace()}
          <form class="feedback-form" id="feedback-form">
            <label for="tester-name">Tester</label>
            <input id="tester-name" name="tester-name" value="${escapeHtml(testerName)}" placeholder="Name" ${
              testerIdentity.authenticated ? 'readonly' : ''
            } />
            ${testerIdentity.authenticated ? '<p class="identity-hint">Autofilled from Cloudflare Zero Trust</p>' : ''}

            <fieldset class="verdict-field">
              <legend>Verdict</legend>
              <label><input type="radio" name="verdict" value="correct" checked /> Correct</label>
              <label><input type="radio" name="verdict" value="wrong" /> Wrong</label>
              <label><input type="radio" name="verdict" value="needs_review" /> Needs review</label>
            </fieldset>

            <fieldset class="tag-field">
              <legend>Issue Tags</legend>
              ${feedbackTags
                .map(
                  (tag) => `
                    <label><input type="checkbox" name="tag" value="${escapeHtml(tag)}" /> ${escapeHtml(tag)}</label>
                  `,
                )
                .join('')}
            </fieldset>

            <label for="feedback-note">Note</label>
            <textarea id="feedback-note" rows="4" placeholder="What should change?"></textarea>

            <button class="primary-action" type="submit" ${lastAnswer ? '' : 'disabled'}>Submit Feedback</button>
            <p class="form-status" id="form-status">${escapeHtml(feedbackFormStatus())}</p>
          </form>

          <div class="feedback-history">
            <div class="panel-subheader">
              <h3>Recent Feedback</h3>
              <span>${feedbackHistory.length}</span>
            </div>
            ${feedbackHistory.length
              ? feedbackHistory
                  .map(
                    (item) => `
                      <div class="history-row">
                        <span>${escapeHtml(item.scenarioId)}</span>
                        <strong>${escapeHtml(item.verdict.replace('_', ' '))}</strong>
                      </div>
                    `,
                  )
                  .join('')
              : '<p class="empty-state">No submissions yet.</p>'}
          </div>
        </aside>
      </main>
    </div>
  `;

  attachEvents();
  scrollMessages();
}

function renderTrace(): string {
  if (!lastAnswer) {
    return `
      <div class="trace-empty">
        <p>No decision run yet.</p>
        <span>Run a scenario or send a prompt to populate the trace.</span>
      </div>
    `;
  }

  return `
    <div class="decision-grid">
      <div class="decision-metric">
        <span>Intent</span>
        <strong>${escapeHtml(lastAnswer.intent.replace('_', ' '))}</strong>
      </div>
      <div class="decision-metric">
        <span>Risk</span>
        <strong>${escapeHtml(lastAnswer.risk)}</strong>
      </div>
      <div class="decision-metric">
        <span>Confidence</span>
        <strong>${Math.round(lastAnswer.confidence * 100)}%</strong>
      </div>
      <div class="decision-metric ${lastAnswer.escalates ? 'is-escalation' : 'is-routine'}">
        <span>Handoff</span>
        <strong>${lastAnswer.escalates ? 'Yes' : 'No'}</strong>
      </div>
    </div>

    <section class="trace-section">
      <h3>Decision Path</h3>
      <ol>
        ${lastAnswer.decisionPath.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}
      </ol>
    </section>

    <section class="trace-section">
      <h3>Sources</h3>
      <div class="source-list">
        ${lastAnswer.citations
          .map(
            (citation) => `
              <span class="source-chip" title="${escapeHtml(citation.source)}">${escapeHtml(citation.label)}</span>
            `,
          )
          .join('')}
      </div>
    </section>

    ${
      lastAnswer.escalationReason
        ? `<section class="trace-section escalation-reason"><h3>Escalation Reason</h3><p>${escapeHtml(lastAnswer.escalationReason)}</p></section>`
        : ''
    }
  `;
}

function attachEvents(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-group]').forEach((button) => {
    button.addEventListener('click', () => {
      selectedGroup = button.dataset.group ?? 'All';
      const next = filteredScenarios()[0] ?? scenarios[0];
      selectedScenario = next;
      lastAnswer = null;
      lastPrompt = '';
      lastPromptRunId = '';
      pendingPromptRun = null;
      activePromptRunToken += 1;
      feedbackStatusMessage = '';
      transcript = [openingMessage()];
      startNewSession();
      render();
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-scenario]').forEach((button) => {
    button.addEventListener('click', () => {
      const next = scenarios.find((scenario) => scenario.id === button.dataset.scenario);
      if (!next) return;
      selectedScenario = next;
      lastAnswer = null;
      lastPrompt = '';
      lastPromptRunId = '';
      pendingPromptRun = null;
      activePromptRunToken += 1;
      feedbackStatusMessage = '';
      transcript = [openingMessage()];
      startNewSession();
      render();
    });
  });

  document.querySelector<HTMLButtonElement>('#run-scenario')?.addEventListener('click', () => {
    void runPrompt(selectedScenario.prompt);
  });

  document.querySelector<HTMLButtonElement>('#reset-chat')?.addEventListener('click', () => {
    transcript = [openingMessage()];
    lastAnswer = null;
    lastPrompt = '';
    lastPromptRunId = '';
    pendingPromptRun = null;
    activePromptRunToken += 1;
    feedbackStatusMessage = '';
    startNewSession();
    render();
  });

  document.querySelector<HTMLFormElement>('#question-form')?.addEventListener('submit', (event) => {
    event.preventDefault();
    const input = document.querySelector<HTMLTextAreaElement>('#question-input');
    void runPrompt(input?.value ?? '');
  });

  document.querySelector<HTMLFormElement>('#feedback-form')?.addEventListener('submit', (event) => {
    event.preventDefault();
    void submitFeedback();
  });
}

async function runPrompt(prompt: string): Promise<void> {
  const trimmed = prompt.trim();
  if (!trimmed) return;

  feedbackStatusMessage = '';
  lastPromptRunId = '';
  transcript.push({ role: 'user', content: trimmed });
  lastPrompt = trimmed;
  lastAnswer = answerHatbotQuestion({
    message: trimmed,
    order: selectedScenario.order,
    product: selectedScenario.product,
  });
  transcript.push({ role: 'assistant', content: lastAnswer.answer });
  render();

  const token = activePromptRunToken + 1;
  activePromptRunToken = token;
  pendingPromptRun = recordPromptRun({
    sessionId: getSessionId(),
    testerName: currentTesterName(),
    scenarioId: selectedScenario.id,
    scenarioTitle: selectedScenario.title,
    prompt: lastPrompt,
    answer: lastAnswer,
    transcript: [...transcript],
    context: currentContext(),
    pageUrl: window.location.href,
  });

  void pendingPromptRun
    .then((id) => {
      if (token === activePromptRunToken) {
        lastPromptRunId = id;
      }
    })
    .catch(() => {
      if (token === activePromptRunToken) {
        feedbackStatusMessage = 'Prompt recording failed';
        updateFormStatus();
      }
    });
}

async function submitFeedback(): Promise<void> {
  const status = document.querySelector<HTMLParagraphElement>('#form-status');
  const testerInput = document.querySelector<HTMLInputElement>('#tester-name');
  const noteInput = document.querySelector<HTMLTextAreaElement>('#feedback-note');
  const verdictInput = document.querySelector<HTMLInputElement>('input[name="verdict"]:checked');
  const tagInputs = Array.from(document.querySelectorAll<HTMLInputElement>('input[name="tag"]:checked'));

  if (!lastAnswer || !lastPrompt) {
    feedbackStatusMessage = 'Run a prompt first';
    if (status) status.textContent = feedbackStatusMessage;
    return;
  }

  const testerName = testerInput?.value.trim() ?? '';
  if (!testerIdentity.authenticated) {
    localStorage.setItem(TESTER_KEY, testerName);
  }

  if (pendingPromptRun && !lastPromptRunId) {
    feedbackStatusMessage = 'Finishing run record';
    if (status) status.textContent = feedbackStatusMessage;
    try {
      lastPromptRunId = await pendingPromptRun;
    } catch {
      lastPromptRunId = '';
    }
  }

  const payload: FeedbackPayload = {
    sessionId: getSessionId(),
    promptRunId: lastPromptRunId,
    testerName,
    scenarioId: selectedScenario.id,
    scenarioTitle: selectedScenario.title,
    prompt: lastPrompt,
    answer: lastAnswer,
    verdict: (verdictInput?.value as Verdict | undefined) ?? 'correct',
    issueTags: tagInputs.map((input) => input.value),
    note: noteInput?.value.trim() ?? '',
    transcript: [...transcript],
    context: currentContext(),
    pageUrl: window.location.href,
  };

  feedbackStatusMessage = 'Submitting';
  if (status) status.textContent = feedbackStatusMessage;

  try {
    const response = await fetch('/api/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = (await response.json()) as { ok?: boolean; id?: string; error?: string };

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Feedback failed');
    }

    writeFeedbackHistory([
      {
        id: data.id ?? crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        scenarioId: selectedScenario.id,
        verdict: payload.verdict,
      },
      ...feedbackHistory,
    ]);

    feedbackStatusMessage = 'Saved';
    render();
  } catch (error) {
    feedbackStatusMessage = error instanceof Error ? error.message : 'Feedback failed';
    if (status) status.textContent = feedbackStatusMessage;
  }
}

function feedbackFormStatus(): string {
  if (feedbackStatusMessage) return feedbackStatusMessage;
  return lastAnswer ? 'Ready' : 'Run a prompt first';
}

async function loadTesterIdentity(): Promise<void> {
  try {
    const response = await fetch('/api/me', { headers: { Accept: 'application/json' } });
    const data = (await response.json()) as {
      ok?: boolean;
      authenticated?: boolean;
      tester?: Partial<TesterIdentity> | null;
    };

    if (response.ok && data.ok && data.authenticated && data.tester) {
      testerIdentity = {
        authenticated: true,
        name: data.tester.name ?? '',
        email: data.tester.email ?? '',
        subject: data.tester.subject ?? '',
      };

      const testerName = currentTesterName();
      if (testerName) {
        localStorage.setItem(TESTER_KEY, testerName);
      }
      render();
    }
  } catch {
    testerIdentity = {
      authenticated: false,
      name: '',
      email: '',
      subject: '',
    };
  }
}

async function recordSession(): Promise<void> {
  try {
    await fetch('/api/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: getSessionId(),
        testerName: currentTesterName(),
        pageUrl: window.location.href,
      }),
    });
  } catch {
    apiState = 'offline';
  }
}

async function recordPromptRun(payload: PromptRunPayload): Promise<string> {
  const response = await fetch('/api/prompt-run', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = (await response.json()) as { ok?: boolean; id?: string; error?: string };

  if (!response.ok || !data.ok || !data.id) {
    throw new Error(data.error || 'Prompt recording failed');
  }

  return data.id;
}

async function checkStatus(): Promise<void> {
  try {
    const response = await fetch('/api/status', { headers: { Accept: 'application/json' } });
    apiState = response.ok ? 'online' : 'offline';
  } catch {
    apiState = 'offline';
  }
  render();
}

function updateFormStatus(): void {
  const status = document.querySelector<HTMLParagraphElement>('#form-status');
  if (status) status.textContent = feedbackFormStatus();
}

function scrollMessages(): void {
  const messages = document.querySelector<HTMLDivElement>('#messages');
  if (messages) messages.scrollTop = messages.scrollHeight;
}

render();
void (async () => {
  await loadTesterIdentity();
  await recordSession();
  await checkStatus();
})();
